import { GPS } from "../../../types";
import { haversineMeters } from "../../../utils/geo";

export interface BoundaryCircle {
  key: string;
  center: GPS;
  radiusM: number;
}

/** A patch of ground covered by more than one location. */
export interface OverlapRegion {
  key: string;
  /** The outline, closed by the renderer. */
  ring: GPS[];
}

const METERS_PER_DEGREE_LAT = 111_320;

/** How many points approximate each arc. Smooth enough at any usable zoom. */
const ARC_STEPS = 32;

/**
 * Whether two locations' coverage overlaps at all.
 *
 * Includes one sitting entirely inside the other, which is just as ambiguous
 * for a trap caught in it as a partial overlap.
 */
export const circlesOverlap = (a: BoundaryCircle, b: BoundaryCircle): boolean =>
  haversineMeters(a.center, b.center) < a.radiusM + b.radiusM;

/**
 * A local flat frame in metres, centred between the two circles.
 *
 * Circle intersection is fiddly on a sphere and unnecessary here: these are
 * radii of a few kilometres at most, where treating the neighbourhood as flat
 * is accurate to well under the width of the line being drawn.
 */
const projection = (origin: GPS) => {
  const lonScale =
    METERS_PER_DEGREE_LAT * Math.cos((origin.lat * Math.PI) / 180);
  return {
    toXY: (p: GPS) => ({
      x: (p.lon - origin.lon) * lonScale,
      y: (p.lat - origin.lat) * METERS_PER_DEGREE_LAT,
    }),
    toGps: (x: number, y: number): GPS => ({
      lat: origin.lat + y / METERS_PER_DEGREE_LAT,
      lon: origin.lon + x / lonScale,
    }),
  };
};

/** An angle folded into [0, 2π). */
const wrapAngle = (a: number): number => {
  const twoPi = Math.PI * 2;
  return ((a % twoPi) + twoPi) % twoPi;
};

/** The smaller of the two ways round between two angles. */
const angularGap = (a: number, b: number): number => {
  const d = Math.abs(wrapAngle(a) - wrapAngle(b));
  return Math.min(d, Math.PI * 2 - d);
};

/**
 * Points along the arc from `from` to `to` that passes through `through`.
 *
 * Two arcs join any pair of points on a circle; the lens is bounded by the one
 * facing the other circle, which is what `through` picks out.
 */
const arc = (
  cx: number,
  cy: number,
  r: number,
  from: number,
  to: number,
  through: number,
): Array<[number, number]> => {
  const ccw = wrapAngle(to - from);
  const sweep =
    angularGap(from + ccw / 2, through) <
    angularGap(from + (ccw - Math.PI * 2) / 2, through)
      ? ccw
      : ccw - Math.PI * 2;

  const points: Array<[number, number]> = [];
  for (let i = 0; i <= ARC_STEPS; i += 1) {
    const angle = from + (sweep * i) / ARC_STEPS;
    points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
  }
  return points;
};

/** A whole circle as a ring, for the case where one contains the other. */
const circleRing = (
  circle: BoundaryCircle,
  project: ReturnType<typeof projection>,
): GPS[] => {
  const { x, y } = project.toXY(circle.center);
  const ring: GPS[] = [];
  for (let i = 0; i < ARC_STEPS * 2; i += 1) {
    const angle = (i * Math.PI * 2) / (ARC_STEPS * 2);
    ring.push(
      project.toGps(
        x + circle.radiusM * Math.cos(angle),
        y + circle.radiusM * Math.sin(angle),
      ),
    );
  }
  return ring;
};

/** The ground two particular locations both claim, or null if they do not. */
export const overlapRegion = (
  a: BoundaryCircle,
  b: BoundaryCircle,
): OverlapRegion | null => {
  const key = [a.key, b.key].sort().join("|");
  const project = projection(a.center);

  const p0 = project.toXY(a.center);
  const p1 = project.toXY(b.center);
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const d = Math.hypot(dx, dy);

  // Rims stop short of each other: nothing is shared.
  if (d >= a.radiusM + b.radiusM) return null;

  // One inside the other: the shared ground is simply the smaller circle.
  if (d <= Math.abs(a.radiusM - b.radiusM)) {
    const inner = a.radiusM <= b.radiusM ? a : b;
    return { key, ring: circleRing(inner, project) };
  }

  // Concentric and equal would be the same circle; d > 0 is guaranteed above
  // for every other case, since d = 0 only survives when the radii differ.
  const chord = (a.radiusM ** 2 - b.radiusM ** 2 + d * d) / (2 * d);
  const half = Math.sqrt(Math.max(a.radiusM ** 2 - chord * chord, 0));
  const mx = p0.x + (chord * dx) / d;
  const my = p0.y + (chord * dy) / d;

  const i1 = { x: mx + (half * dy) / d, y: my - (half * dx) / d };
  const i2 = { x: mx - (half * dy) / d, y: my + (half * dx) / d };

  const angleFrom = (
    c: { x: number; y: number },
    p: { x: number; y: number },
  ) => Math.atan2(p.y - c.y, p.x - c.x);

  // Each arc bulges toward the other circle's centre.
  const ring = [
    ...arc(
      p0.x,
      p0.y,
      a.radiusM,
      angleFrom(p0, i1),
      angleFrom(p0, i2),
      angleFrom(p0, p1),
    ),
    ...arc(
      p1.x,
      p1.y,
      b.radiusM,
      angleFrom(p1, i2),
      angleFrom(p1, i1),
      angleFrom(p1, p0),
    ),
  ].map(([x, y]) => project.toGps(x, y));

  return { key, ring };
};

/**
 * Every patch of ground claimed by more than one location.
 *
 * Worth showing rather than merely drawing the circles: a trap standing in an
 * overlap could reasonably belong to either location, so neither the "too far
 * away" warning nor the suggestion of a better home means quite what it says
 * there.
 */
export const overlapRegions = (
  circles: readonly BoundaryCircle[],
): OverlapRegion[] => {
  const regions: OverlapRegion[] = [];
  for (let i = 0; i < circles.length; i += 1) {
    for (let j = i + 1; j < circles.length; j += 1) {
      const region = overlapRegion(circles[i], circles[j]);
      if (region) regions.push(region);
    }
  }
  return regions;
};
